import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Plus } from 'lucide-react';
import { cn } from '../../lib/utils';
import { SpecraButton } from '../specra/ensigns';
/**
 * PageEmptyState displays a centered empty state with icon, title, description, and optional action.
 * Two sizes available:
 * - 'lg': For when there's no data at all (larger icon, more padding)
 * - 'sm': For when a filter returns no results (smaller icon, less padding)
 *
 * @example
 * // Page-level empty state
 * <PageEmptyState
 *   icon={<FolderOpen />}
 *   title="No projects yet"
 *   description="Create your first project to get started."
 *   action={{ label: "Create first project", onClick: openDialog }}
 *   size="lg"
 * />
 *
 * @example
 * // Filter-level empty state
 * <PageEmptyState
 *   icon={<Search />}
 *   title="No matching items"
 *   description="Try adjusting your search terms"
 *   size="sm"
 * />
 */ export function PageEmptyState({ icon, title, description, action, size = 'lg', className, dataTestid }) {
    const isLarge = size === 'lg';
    return /*#__PURE__*/ _jsxs("div", {
        "data-testid": dataTestid,
        className: cn('flex flex-col items-center justify-center px-4', isLarge ? 'py-20' : 'py-16', className),
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: cn('bg-gray-100 rounded-full mb-4', isLarge ? 'p-6' : 'p-4'),
                children: /*#__PURE__*/ _jsx("div", {
                    className: cn('text-gray-400', isLarge ? '[&>svg]:w-12 [&>svg]:h-12' : '[&>svg]:w-8 [&>svg]:h-8'),
                    children: icon
                })
            }),
            isLarge ? /*#__PURE__*/ _jsx("h2", {
                className: "text-xl font-semibold text-gray-900 mb-2",
                children: title
            }) : /*#__PURE__*/ _jsx("h3", {
                className: "text-lg font-medium text-gray-900 mb-1",
                children: title
            }),
            /*#__PURE__*/ _jsx("p", {
                className: cn('text-center', isLarge ? 'text-gray-600 mb-6 max-w-md' : 'text-sm text-gray-500 max-w-sm'),
                children: description
            }),
            action && /*#__PURE__*/ _jsxs(SpecraButton, {
                onClick: action.onClick,
                className: isLarge ? '' : 'mt-4',
                "data-testid": action.dataTestid,
                children: [
                    action.icon ?? /*#__PURE__*/ _jsx(Plus, {
                        className: "w-4 h-4 mr-2"
                    }),
                    action.label
                ]
            })
        ]
    });
}
